import React, { useState, useEffect, useContext } from 'react';
import { collection, query, orderBy, onSnapshot, doc, setDoc } from 'firebase/firestore';
import { Home, PlusCircle, List, BarChart2, Bell } from 'lucide-react';

import { FirebaseContext } from '../App';
import { firebaseAppId } from '../configs/firebaseConfig'; // firebaseAppIdをインポート

const Dashboard = ({ setCurrentPage }) => {
    const { db, userId } = useContext(FirebaseContext);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        if (!db || !userId) return;

        const fetchTransactionsAndSettings = async () => {
            setLoading(true);
            try {
                const transactionsColRef = collection(db, `artifacts/${firebaseAppId}/users/${userId}/transactions`);
                const q = query(transactionsColRef, orderBy('date', 'desc'));
                const unsubscribeTransactions = onSnapshot(q, (snapshot) => {
                    const fetchedTransactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setTransactions(fetchedTransactions);
                });

                const userSettingsDocRef = doc(db, `artifacts/${firebaseAppId}/users/${userId}/settings`, 'user_settings');
                const unsubscribeSettings = onSnapshot(userSettingsDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        setSettings(docSnap.data());
                    } else {
                        setSettings({
                            notificationSettings: {
                                dailyReminder: { enabled: true, time: '10:00' },
                                weeklyReminder: { enabled: true, day: 1, time: '09:00' },
                                monthlyReminder: { enabled: true, date: 1, time: '09:30' },
                                taxFilingReminder: { enabled: true, preDeadline: true, deadline: true },
                                consumptionTaxReminder: { enabled: true, preDeadline: true, deadline: true },
                                incompleteTransactionReminder: { enabled: true }
                            }
                        });
                    }
                });

                setLoading(false);
                return () => {
                    unsubscribeTransactions();
                    unsubscribeSettings();
                };
            } catch (err) {
                console.error("ダッシュボードデータの取得エラー:", err);
                setLoading(false);
            }
        };
        fetchTransactionsAndSettings();
    }, [db, userId]);

    useEffect(() => {
        if (!settings || !transactions.length) return;

        const generateNotifications = () => {
            const today = new Date();
            const currentNotifications = [];

            const notificationSettings = settings.notificationSettings || {};

            // Daily Reminder
            if (notificationSettings.dailyReminder?.enabled) {
                const reminderTime = notificationSettings.dailyReminder.time;
                const [hour, minute] = reminderTime.split(':').map(Number);
                const reminderDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute);

                if (today.toDateString() === reminderDate.toDateString() && today.getHours() === hour && today.getMinutes() >= minute) {
                    currentNotifications.push({ id: 'daily', message: `今日の取引を入力しましょう！`, type: 'info' });
                }
            }

            // Weekly Reminder
            if (notificationSettings.weeklyReminder?.enabled) {
                const dayOfWeek = notificationSettings.weeklyReminder.day;
                if (today.getDay() === dayOfWeek) {
                     currentNotifications.push({ id: 'weekly', message: `今週の支出を確認しましょう。`, type: 'info' });
                }
            }

            // Monthly Reminder
            if (notificationSettings.monthlyReminder?.enabled) {
                const dateOfMonth = notificationSettings.monthlyReminder.date;
                if (today.getDate() === dateOfMonth) {
                    currentNotifications.push({ id: 'monthly', message: `今月の売上を集計しましょう。`, type: 'info' });
                }
            }

            // 特定の期日に合わせた通知 (Mock Dates)
            const mockTaxDeadline = new Date(today.getFullYear() + (today.getMonth() > 2 ? 1 : 0), 2, 15);
            const mockConsumptionTaxDeadline = new Date(today.getFullYear() + (today.getMonth() > 2 ? 1 : 0), 3, 30);

            const daysUntilTax = Math.ceil((mockTaxDeadline - today) / (1000 * 60 * 60 * 24));
            const daysUntilConsumptionTax = Math.ceil((mockConsumptionTaxDeadline - today) / (1000 * 60 * 60 * 24));

            if (notificationSettings.taxFilingReminder?.enabled) {
                if (daysUntilTax <= 60 && daysUntilTax > 30 && notificationSettings.taxFilingReminder.preDeadline) {
                    currentNotifications.push({ id: 'tax_prep', message: `確定申告の準備期間に入りました。`, type: 'warning' });
                }
                if (daysUntilTax <= 14 && daysUntilTax > 0 && notificationSettings.taxFilingReminder.deadline) {
                    currentNotifications.push({ id: 'tax_deadline_soon', message: `確定申告の提出期限が迫っています！残り${daysUntilTax}日`, type: 'danger' });
                }
                if (daysUntilTax === 0 && today.toDateString() === mockTaxDeadline.toDateString()) {
                    currentNotifications.push({ id: 'tax_deadline_today', message: `本日、確定申告の提出期限です！`, type: 'danger' });
                }
            }

            if (notificationSettings.consumptionTaxReminder?.enabled) {
                if (daysUntilConsumptionTax <= 60 && daysUntilConsumptionTax > 30 && notificationSettings.consumptionTaxReminder.preDeadline) {
                    currentNotifications.push({ id: 'consumption_tax_prep', message: `消費税申告の準備期間に入りました。`, type: 'warning' });
                }
                if (daysUntilConsumptionTax <= 14 && daysUntilConsumptionTax > 0 && notificationSettings.consumptionTaxReminder.deadline) {
                    currentNotifications.push({ id: 'consumption_tax_deadline_soon', message: `消費税申告の提出期限が迫っています！残り${daysUntilConsumptionTax}日`, type: 'danger' });
                }
                if (daysUntilConsumptionTax === 0 && today.toDateString() === mockConsumptionTaxDeadline.toDateString()) {
                    currentNotifications.push({ id: 'consumption_tax_deadline_today', message: `本日、消費税申告の提出期限です！`, type: 'danger' });
                }
            }

            // 不備や未完了事項に関する通知 (Simplified checks)
            if (notificationSettings.incompleteTransactionReminder?.enabled) {
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(today.getDate() - 7);
                const recentTransactions = transactions.filter(t => t.date?.toDate() >= oneWeekAgo);
                if (recentTransactions.length === 0) {
                    currentNotifications.push({ id: 'no_recent_transactions', message: `最近の取引が入力されていません。確認してください。`, type: 'warning' });
                }

                const totalCashIn = transactions.filter(t => t.paymentMethod === '現金' && t.type === '収入').reduce((acc, t) => acc + t.amount, 0);
                const totalCashOut = transactions.filter(t => t.paymentMethod === '現金' && t.type === '支出').reduce((acc, t) => acc + t.amount, 0);
                if (totalCashOut > totalCashIn) {
                     currentNotifications.push({ id: 'negative_cash', message: `現金残高がマイナスになっている可能性があります。取引を確認してください。`, type: 'danger' });
                }
            }

            setNotifications(currentNotifications);
        };

        generateNotifications();
        const intervalId = setInterval(generateNotifications, 60000);

        return () => clearInterval(intervalId);
    }, [settings, transactions]);

    const income = transactions.filter(t => t.type === '収入').reduce((acc, t) => acc + t.amount, 0);
    const expense = transactions.filter(t => t.type === '支出').reduce((acc, t) => acc + t.amount, 0);
    const profit = income - expense;

    return (
        <div className="space-y-8">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">ダッシュボード</h2>

            {loading && <div className="text-center text-gray-600">データを読み込み中...</div>}
            {!loading && (
                <>
                    {/* 概要カード - モバイルでは1列、PCでは3列 */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">総収入</h3>
                            <p className="text-4xl font-bold text-blue-600">¥{income.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">総支出</h3>
                            <p className="text-4xl font-bold text-red-600">¥{expense.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">純利益</h3>
                            <p className="text-4xl font-bold text-green-600">¥{profit.toLocaleString()}</p>
                        </div>
                    </div>

                    {/* 通知セクション */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                        <h3 className="text-2xl font-semibold text-gray-800 flex items-center gap-2 mb-4">
                            <Bell size={24} className="text-yellow-500" />
                            通知
                        </h3>
                        {notifications.length === 0 ? (
                            <p className="text-gray-600">新しい通知はありません。</p>
                        ) : (
                            <ul className="space-y-3">
                                {notifications.map((notif, index) => (
                                    <li key={notif.id || index} className={`p-4 rounded-lg flex items-center gap-3 ${notif.type === 'danger' ? 'bg-red-50 text-red-800 border border-red-200' : notif.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}>
                                        <Bell size={20} />
                                        <p className="font-medium">{notif.message}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <button onClick={() => setCurrentPage('settings')} className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-200 ease-in-out shadow-md">
                            通知設定を変更
                        </button>
                    </div>

                    {/* クイックアクション - モバイルでは1列、PCでは3列 */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-4">クイックアクション</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <button onClick={() => setCurrentPage('transactionInput')} className="flex flex-col items-center justify-center p-6 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition duration-200 ease-in-out transform hover:scale-105 shadow-md">
                                <PlusCircle size={36} className="mb-2" />
                                <span className="text-lg font-medium">新しい取引を入力</span>
                            </button>
                            <button onClick={() => setCurrentPage('transactionList')} className="flex flex-col items-center justify-center p-6 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition duration-200 ease-in-out transform hover:scale-105 shadow-md">
                                <List size={36} className="mb-2" />
                                <span className="text-lg font-medium">取引履歴を見る</span>
                            </button>
                            <button onClick={() => setCurrentPage('reports')} className="flex flex-col items-center justify-center p-6 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition duration-200 ease-in-out transform hover:scale-105 shadow-md">
                                <BarChart2 size={36} className="mb-2" />
                                <span className="text-lg font-medium">レポートを生成</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
